import { FunctionComponent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import Select, { Option } from '../forms/Select';
import { PropertyAssociationInfo } from '../propertyAssociation/models/PropertyAssociationInfo';
import { usePropertyAssociationContext } from '../propertyAssociation/PropertyAssociationContext';
import { NO_AREA_PLACEHOLDER } from '../propertyAssociation/propertyAssociationEnums';
import { useNavigationPaths } from '../propertyAssociation/useNavigationPaths';

type Props = {
    heading: string;
};

const SubNavigationHeading: FunctionComponent<Props> = ({ heading }) => {
    const { currentPropertyAssociation, currentArea, allPropertyAssociations, allAreas } = usePropertyAssociationContext();
    const navigate = useNavigate();
    const paths = useNavigationPaths();
    const { pathname } = useLocation();
    const { areaId, propertyAssociationId } = useParams();

    const changePropertyAssociation = (newPropertyAssociationId: string) => {
        if (areaId) {
            // Area-based page: extract the page path after areaId to return to after area selection
            const page = pathname.split(areaId).at(-1);
            navigate(paths.propertyAssociation(newPropertyAssociationId), { state: { returnPath: page } });
        } else if (propertyAssociationId) {
            // Non-area page (settings, members, messages): go directly to the same page in the new PA
            const page = pathname.split(propertyAssociationId).at(-1) ?? '';
            navigate(`/${newPropertyAssociationId}${page}`);
        } else {
            navigate(paths.propertyAssociation(newPropertyAssociationId));
        }
    };

    const changeArea = (newAreaId: string) => {
        // Store the selected area in sessionStorage for persistence across navigation
        if (currentPropertyAssociation?.propertyAssociationId) {
            sessionStorage.setItem(`lastAreaId-${currentPropertyAssociation.propertyAssociationId}`, newAreaId);
        }

        if (areaId) {
            const page = pathname.split(areaId ?? NO_AREA_PLACEHOLDER).at(-1);
            navigate(paths.area(newAreaId, page));
            return;
        }

        // For non-area pages, pass the new area ID via navigation state to trigger a re-render
        navigate('.', { replace: true, state: { selectedAreaId: newAreaId } });
    };

    const areas: Option[] = [{ label: 'Välj område', value: NO_AREA_PLACEHOLDER, disabled: true }, ...allAreas.map((area) => ({ label: area.name, value: area.propertyAssociationAreaId }))];
    const isMyAssociation = allPropertyAssociations.some((pa) => pa.propertyAssociationId === currentPropertyAssociation?.propertyAssociationId);
    const propertyAssociations = [isMyAssociation ? undefined : currentPropertyAssociation!, ...allPropertyAssociations].filter(Boolean) as NonNullable<PropertyAssociationInfo[]>;

    return (
        <div className="submenu__header">
            {allPropertyAssociations.length > 1 ? (
                <Select
                    label="Engagemang"
                    options={propertyAssociations.map((pa) => ({ label: pa.name, value: pa.propertyAssociationId }))}
                    value={currentPropertyAssociation?.propertyAssociationId}
                    onChange={(e) => changePropertyAssociation(e.target.value)}
                    size="sm"
                />
            ) : (
                <strong>{currentPropertyAssociation?.name}</strong>
            )}
            {allAreas.length > 1 ? (
                <Select label="Område" options={areas} value={currentArea?.propertyAssociationAreaId || NO_AREA_PLACEHOLDER} onChange={(e) => changeArea(e.target.value)} size="sm" />
            ) : currentArea ? (
                <span>{currentArea.name}</span>
            ) : null}
            {heading}
        </div>
    );
};

export default SubNavigationHeading;
